import { z } from "zod";

/**
 * Kennzahlen-Leiste über der Auftragsliste. Welche Kennzahlen Kunden sehen, legt Logiful zentral fest
 * (Logiful intern → Kennzahlen) – manche Werte (Pünktlichkeit, Bearbeitungszeit) machen uns angreifbar und sind
 * deshalb standardmäßig aus. Abgeschaltete Kennzahlen berechnet der Server nicht und liefert sie nicht aus.
 */
export const ORDER_KPI_KEYS = ["orders", "items", "value", "shipped", "delivered", "on_hold", "on_time", "handover_to_ship", "ship_to_delivery"] as const;
export type OrderKpiKey = (typeof ORDER_KPI_KEYS)[number];

export type OrderKpiUnit = "count" | "money" | "percent" | "hours" | "days";

export interface OrderKpiDef {
  key: OrderKpiKey;
  unit: OrderKpiUnit;
  /** Momentaufnahme ohne Verlauf (z. B. „zurückgehalten“) */
  snapshot?: boolean;
  /** heikel: Leistungswert, der uns angreifbar machen kann – Standard aus */
  sensitive?: boolean;
}

/** Bezeichnungen und Erklärungen stehen im Portal (übersetzbar) */
export const ORDER_KPIS: Record<OrderKpiKey, OrderKpiDef> = {
  orders: { key: "orders", unit: "count" },
  items: { key: "items", unit: "count" },
  value: { key: "value", unit: "money" },
  shipped: { key: "shipped", unit: "count" },
  delivered: { key: "delivered", unit: "count" },
  on_hold: { key: "on_hold", unit: "count", snapshot: true },
  on_time: { key: "on_time", unit: "percent", sensitive: true },
  handover_to_ship: { key: "handover_to_ship", unit: "hours", sensitive: true },
  ship_to_delivery: { key: "ship_to_delivery", unit: "days", sensitive: true },
};

export const orderKpiSettingsSchema = z.object({
  /** angezeigte Kennzahlen in dieser Reihenfolge */
  enabled: z.array(z.enum(ORDER_KPI_KEYS)).max(ORDER_KPI_KEYS.length).transform((xs) => [...new Set(xs)]),
});
export type OrderKpiSettings = z.infer<typeof orderKpiSettingsSchema>;
export const DEFAULT_ORDER_KPIS: OrderKpiSettings = { enabled: ["orders", "items", "shipped", "delivered", "on_hold"] };

export const ORDER_KPI_RANGES = ["today", "7d", "30d"] as const;
export type OrderKpiRange = (typeof ORDER_KPI_RANGES)[number];

export interface OrderKpiValue {
  key: OrderKpiKey;
  /** Wert im Zeitraum (null = keine Grundlage, z. B. Durchschnitt ohne Aufträge) */
  value: number | null;
  /** Verlauf: je Stunde (heute) bzw. je Tag – leer bei Momentaufnahmen */
  series: (number | null)[];
}
